import React, { useState } from "react";

// 뉴스레터 생성 화면에서 입력하는 내용이 실제 앱에서 어떻게 보이는지
// 실시간으로 보여주는 휴대폰 목업 미리보기.
//
// props:
//   pageNames  : ["1페이지명","2페이지명","3페이지명"]  (배열 또는 {page1,page2,page3})
//   date       : 발행 날짜 문자열
//   noOfWeeks  : 주차
//   magazine   : { page1:{image,imageText,tableArray}, page2:[{header,text}], page3:{header,text} }
//   activePage : 처음 보여줄 탭 (0=1페이지, 1=2페이지, 2=3페이지)

function normalizePageNames(pageNames) {
  if (Array.isArray(pageNames)) return pageNames;
  if (pageNames && typeof pageNames === "object") {
    return [pageNames.page1, pageNames.page2, pageNames.page3];
  }
  return ["", "", ""];
}

function formatDate(date, noOfWeeks) {
  var label = "";
  if (date) {
    var parsed = Date.parse(date);
    label = isNaN(parsed)
      ? String(date)
      : new Date(parsed).toUTCString().split("00:00")[0];
  }
  if (noOfWeeks) {
    label += (label ? " · " : "") + noOfWeeks + "주차";
  }
  return label || "발행 날짜 미입력";
}

var emptyText = "내용을 입력하면\n여기에 표시됩니다.";

function Empty(props) {
  return (
    <div className="mt-8 whitespace-pre-line text-center text-xs italic text-gray-300">
      {props.text || emptyText}
    </div>
  );
}

function SectionCard(props) {
  return (
    <div className="mb-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
      {props.header ? (
        <div className="mb-1 border-b border-gray-200 pb-1 text-sm font-bold text-gray-800">
          {props.header}
        </div>
      ) : (
        <div className="mb-1 border-b border-gray-200 pb-1 text-sm font-bold text-gray-300">
          (소제목 없음)
        </div>
      )}
      {props.children}
    </div>
  );
}

function renderPage1(p1) {
  var tableArray = p1.tableArray || [];
  var hasAny =
    p1.image ||
    p1.imageText ||
    tableArray.some(function(t) {
      return (
        t.header ||
        (t.rows || []).some(function(r) {
          return r.column1 || r.column2;
        })
      );
    });

  if (!hasAny) return <Empty />;

  return (
    <div>
      {p1.image ? (
        <img
          src={p1.image}
          alt=""
          className="mb-3 w-full rounded-lg object-cover"
          style={{ maxHeight: 140 }}
        />
      ) : (
        <div className="mb-3 flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-gray-200 text-xs text-gray-300">
          대표 이미지
        </div>
      )}

      {p1.imageText ? (
        <p className="mb-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
          {p1.imageText}
        </p>
      ) : null}

      {tableArray.map(function(table, i) {
        return (
          <SectionCard key={i} header={table.header}>
            {(table.rows || []).map(function(row, j) {
              if (!row.column1 && !row.column2) return null;
              return (
                <div key={j} className="mb-2 last:mb-0">
                  {row.column1 ? (
                    <div className="text-sm font-medium text-gray-800">
                      {row.column1}
                    </div>
                  ) : null}
                  {row.column2 ? (
                    <div className="whitespace-pre-wrap text-sm text-gray-600">
                      {row.column2}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </SectionCard>
        );
      })}
    </div>
  );
}

function renderPage2(p2) {
  var sections = p2 || [];
  var hasAny = sections.some(function(s) {
    return s.header || s.text;
  });
  if (!hasAny) return <Empty />;

  return (
    <div>
      {sections.map(function(section, i) {
        return (
          <SectionCard key={i} header={section.header}>
            {section.text ? (
              <p className="whitespace-pre-wrap text-sm text-gray-600">
                {section.text}
              </p>
            ) : (
              <p className="text-xs italic text-gray-300">(내용 없음)</p>
            )}
          </SectionCard>
        );
      })}
    </div>
  );
}

function renderPage3(p3) {
  var page3 = p3 || {};
  if (!page3.header && !page3.text) return <Empty />;

  return (
    <SectionCard header={page3.header}>
      {page3.text ? (
        <div
          className="text-sm leading-relaxed text-gray-600"
          dangerouslySetInnerHTML={{ __html: page3.text }}
        />
      ) : (
        <p className="text-xs italic text-gray-300">(내용 없음)</p>
      )}
    </SectionCard>
  );
}

export default function NewsletterPhonePreview(props) {
  var names = normalizePageNames(props.pageNames);
  var magazine = props.magazine || {};
  var initial = typeof props.activePage === "number" ? props.activePage : 0;
  var tabState = useState(initial);
  var tab = tabState[0];
  var setTab = tabState[1];

  function tabLabel(i) {
    return names[i] && names[i].trim() ? names[i] : i + 1 + "페이지";
  }

  var content;
  if (tab === 0) content = renderPage1(magazine.page1 || {});
  else if (tab === 1) content = renderPage2(magazine.page2 || []);
  else content = renderPage3(magazine.page3 || {});

  return (
    <div className="mx-auto" style={{ width: 320 }}>
      {/* 휴대폰 베젤 */}
      <div className="rounded-[2.5rem] bg-gray-900 p-2.5 shadow-2xl">
        <div
          className="relative flex flex-col overflow-hidden rounded-[2rem] bg-white"
          style={{ height: 620 }}
        >
          {/* 노치 */}
          <div className="absolute left-1/2 top-0 z-20 h-5 w-28 -translate-x-1/2 rounded-b-2xl bg-gray-900" />

          {/* 앱 헤더 */}
          <div className="shrink-0 bg-gradient-to-r from-sky-500 to-cyan-400 px-4 pb-3 pt-7 text-white">
            <div className="text-[11px] opacity-90">
              {formatDate(props.date, props.noOfWeeks)}
            </div>
            <div className="truncate text-base font-bold">{tabLabel(tab)}</div>
          </div>

          {/* 내용 (스크롤) */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3">
            {content}
          </div>

          {/* 하단 탭 바 */}
          <div className="grid shrink-0 grid-cols-3 border-t border-gray-200 bg-white">
            {[0, 1, 2].map(function(i) {
              var active = i === tab;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={function() {
                    setTab(i);
                  }}
                  className={
                    "flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition " +
                    (active ? "text-sky-600" : "text-gray-400 hover:text-gray-600")
                  }
                >
                  <span
                    className={
                      "flex h-5 w-5 items-center justify-center rounded-full text-[10px] " +
                      (active ? "bg-sky-500 text-white" : "bg-gray-200 text-gray-500")
                    }
                  >
                    {i + 1}
                  </span>
                  <span className="max-w-[90px] truncate">{tabLabel(i)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="mt-2 text-center text-xs text-gray-400">
        📱 실제 앱 미리보기 · 탭을 눌러 페이지를 전환해 보세요
      </p>
    </div>
  );
}
